"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo } from "react";
import { sourceFallbackTitle } from "@/components/assistant-ui/sourceText";
import { useProjectDocuments } from "@/hooks/useProjectDocuments";
import type { MentionItem } from "@/lib/mentions/types";
import { useProjectLaunchContextCatalogStore } from "@/stores/useProjectLaunchContextCatalogStore";
import type * as experimentService from "@/services/experimentService";
import type * as insightService from "@/services/insightService";
type InsightRow = Awaited<ReturnType<typeof insightService.fetchInsights>>[number];
type ExperimentRow = Awaited<ReturnType<typeof experimentService.fetchExperiments>>[number];
function isMentionableInsight(item: InsightRow): boolean {
  return item.status === "VALIDATED";
}
function isMentionableExperiment(item: ExperimentRow): boolean {
  return item.status !== "DRAFT";
}
function documentDescription(indexingStatus: string | undefined): string | undefined {
  if (indexingStatus === "PENDING") return "Indexing…";
  if (indexingStatus === "FAILED") return "Indexing failed";
  return "Project library";
}

/**
 * Project-wide catalog for the home launch context picker (no workbench path filter).
 * Insights and experiments are cached per project (30s TTL) via Zustand store.
 */
export function useProjectLaunchContextCatalog(projectId) {
  const $ = _c(22);
  const {
    documents,
    loading: documentsLoading,
    error: documentsError
  } = useProjectDocuments(projectId);
  let t0;
  if ($[0] !== projectId) {
    t0 = s => s.byProject[projectId];
    $[0] = projectId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const entitySlice = useProjectLaunchContextCatalogStore(t0);
  const loadCatalog = useProjectLaunchContextCatalogStore(_temp);
  let t1;
  let t2;
  if ($[2] !== loadCatalog || $[3] !== projectId) {
    t1 = () => {
      loadCatalog(projectId);
    };
    t2 = [projectId, loadCatalog];
    $[2] = loadCatalog;
    $[3] = projectId;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  if ($[6] !== entitySlice?.insights) {
    t3 = entitySlice?.insights ?? [];
    $[6] = entitySlice?.insights;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const insights = t3;
  let t4;
  if ($[8] !== entitySlice?.experiments) {
    t4 = entitySlice?.experiments ?? [];
    $[8] = entitySlice?.experiments;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const experiments = t4;
  const entityLoading = entitySlice?.loading ?? false;
  const entityError = entitySlice?.error ?? null;
  let t5;
  if ($[10] !== documents) {
    t5 = documents.map(_temp2);
    $[10] = documents;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const documentItems = t5;
  let t6;
  if ($[12] !== insights) {
    t6 = insights.filter(isMentionableInsight).map(_temp3);
    $[12] = insights;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const insightItems = t6;
  let t7;
  if ($[14] !== experiments) {
    t7 = experiments.filter(isMentionableExperiment).map(_temp4);
    $[14] = experiments;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const experimentItems = t7;
  const t8 = documentsLoading || entityLoading;
  const t9 = documentsError ?? entityError;
  let t10;
  if ($[16] !== documentItems || $[17] !== experimentItems || $[18] !== insightItems || $[19] !== t8 || $[20] !== t9) {
    t10 = {
      documentItems,
      insightItems,
      experimentItems,
      loading: t8,
      error: t9
    };
    $[16] = documentItems;
    $[17] = experimentItems;
    $[18] = insightItems;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}
function _temp4(item_0) {
  return {
    id: item_0.id,
    type: "experiment",
    label: item_0.title?.trim() || sourceFallbackTitle("experiment"),
    description: item_0.status ?? undefined
  };
}
function _temp3(item) {
  return {
    id: item.id,
    type: "insight",
    label: item.insight?.trim() || sourceFallbackTitle("insight"),
    description: "Validated insight"
  };
}
function _temp2(doc) {
  return {
    id: doc.id,
    type: "document",
    label: doc.documentName,
    description: documentDescription(doc.indexingStatus)
  };
}
function _temp(s_0) {
  return s_0.load;
}